import { useEffect, useMemo } from 'react';
import type { AccommodationsConsoleState, AccommodationsScenario } from '@shared/types/Accommodations.types';
import { useAccommodationPlacesByCouple } from './useAccommodationPlacesByCouple';
import { useRegionalHotels } from './useRegionalHotels';
import { useBackgroundPricingRefresh } from './useBackgroundPricingRefresh';
import type { CashFundConfig } from '@shared/utils/cashFund/cashFund.types';
import type { FeaturedPlacementVariant } from './useFeaturedPlacements.types';
import { resolveMaxSponsoredSlots } from '../featuredPlacementSlots';

type UseAccommodationsConsoleListingsArgs = Readonly<{
  state: AccommodationsConsoleState;
  variant: 'couple' | 'regional';
  skipCouplePicks?: boolean;
  shouldFilterOutNonJoyRoomBlocks: boolean;
  eventHandle: Maybe<string>;
  onError?: (error: unknown) => void;
  onSuccess?: () => void;
  isInitialLoad: boolean;
  /** Set only by the cash-fund variant; turns on each listing's server-computed fund/card split. */
  cashFund?: CashFundConfig;
  /**
   * Featured-placement A/B test — set for the `guest` scenario only, the one surface the treatments
   * render on. The variant decides how many sponsored slots the search asks for, so it has to be
   * resolved before the query fires (see AccommodationsConsole).
   */
  featuredPlacementVariant?: FeaturedPlacementVariant;
  /**
   * Which console surface this is. Decides the sponsored-slot request together with the variant —
   * see `resolveMaxSponsoredSlots`, which is the only place that choice is made — and is reported on
   * `destinationSearched` so fill rate can be read per surface.
   */
  scenario?: AccommodationsScenario;
  /** The page's own tags; decides the first request's fetch strategy before the URL is seeded. */
  defaultPrioritizeTags?: ReadonlyArray<string>;
}>;

export const useAccommodationsConsoleListings = (args: UseAccommodationsConsoleListingsArgs) => {
  const {
    state,
    variant,
    skipCouplePicks = false,
    shouldFilterOutNonJoyRoomBlocks,
    eventHandle,
    onError,
    onSuccess,
    isInitialLoad,
    cashFund,
    featuredPlacementVariant,
    scenario,
    defaultPrioritizeTags,
  } = args;

  const isViewingCouplesPicks = variant === 'couple' && !skipCouplePicks;

  // Scenario-aware on purpose: the two hotel-search consoles ask for what their arm says, and admin,
  // cashFund and honeymoon always ask for zero. `resolveMaxSponsoredSlots` owns that decision for
  // every surface. A caller that names no scenario omits the field, which the shipped contract
  // treats as zero — the same fail-closed answer, just not one we can state on its behalf.
  const maxSponsoredSlots = scenario === undefined ? undefined : resolveMaxSponsoredSlots(scenario, featuredPlacementVariant);

  const { eventAccommodations, isFetchingEventAccommodations, errorFetchingAccommodationPrices } = useAccommodationPlacesByCouple({
    state,
    eventHandle,
    skip: skipCouplePicks,
    shouldFilterOutNonJoyRoomBlocks,
  });

  const {
    payload,
    cachedListings,
    hasReceivedRegionalData,
    isFetchingListings,
    error: regionalError,
  } = useRegionalHotels({
    state,
    isInitialLoad,
    eventHandle,
    cashFund,
    maxSponsoredSlots,
    defaultPrioritizeTags,
  });

  const {
    error: backgroundError,
    listings: backgroundListings,
    isLoading: isBackgroundPricingLoading,
  } = useBackgroundPricingRefresh({
    isFetchingListings,
    hasReceivedRegionalData,
    regionalHotelsPayload: payload,
    locationName: state.searchLocation,
    cashFund,
    featuredPlacementVariant,
    scenario,
  });

  const hasAnyError = Boolean(regionalError || backgroundError);
  // Only treat primary regional listing errors as API errors that should show error state
  // Background pricing refresh failures are non-critical and shouldn't trigger error UI
  const hasApiError = Boolean(regionalError);
  const areQueriesSettled = !isFetchingListings && !isBackgroundPricingLoading;
  const shouldUseBackgroundListings = areQueriesSettled && !backgroundError && backgroundListings.length > 0;

  const consolidatedListings = useMemo(() => {
    const regionalListings = shouldUseBackgroundListings ? backgroundListings : cachedListings;

    if (skipCouplePicks) {
      return regionalListings;
    }

    const searchScoreByJoyPlaceId = new Map(regionalListings.filter(l => l.joyPlaceId).map(l => [l.joyPlaceId, l.searchScore]));

    const couplePickJoyPlaceIds = new Set<string>(eventAccommodations.filter(x => x.joyPlaceId).map(x => x.joyPlaceId) as string[]);

    const regionalListingsJoyPlaceIds = new Set<string>(regionalListings.filter(l => l.joyPlaceId).map(l => l.joyPlaceId as string));
    const couplesPickToShow = isInitialLoad
      ? eventAccommodations
      : eventAccommodations.filter(accommodation => accommodation.joyPlaceId && regionalListingsJoyPlaceIds.has(accommodation.joyPlaceId));

    return [
      ...couplesPickToShow.map(accommodation => {
        const matchingRegionalListing = regionalListings.find(listing => listing.joyPlaceId === accommodation.joyPlaceId);
        // The refresh (prefer-vendor) can omit a hotel the first paint (snapshots) had. Its tags came
        // from the regional search, so without this fallback a tagged couple's pick would lose them
        // when the refresh settles and be filtered out of a tag-filtered page it belonged on.
        const firstPaintListing = matchingRegionalListing ?? cachedListings.find(listing => listing.joyPlaceId === accommodation.joyPlaceId);
        const updates = {
          searchScore: accommodation.joyPlaceId ? searchScoreByJoyPlaceId.get(accommodation.joyPlaceId) : undefined,
          otaProvider: matchingRegionalListing?.otaProvider,
          // Prefer couple pick's own chain (from secondary fetch), fall back to regional listing
          chain: accommodation.chain ?? matchingRegionalListing?.chain ?? null,
          // Carried across, because only the regional search asks for tags: the couple's-picks query
          // does not request them, so substituting its DTO here would drop `tags` and a tagged hotel
          // that is ALSO a couple's pick would silently be filtered out (useListingSort matches on
          // this field). Own value first for when that query does start returning them.
          tags: accommodation.tags ?? firstPaintListing?.tags,
        };
        // Regional results never include room blocks, and the fund cannot pay for one anyway.
        if (accommodation.type !== 'standardHotel') {
          return { ...accommodation, ...updates };
        }
        // Only the regional search selects the fund/card split, and the server computed it against
        // that tile's rate. A matched pick borrows both together: its own price under a split for
        // another total would not add up.
        if (matchingRegionalListing?.walletSpend && matchingRegionalListing.pricingInfo.values) {
          return { ...accommodation, ...updates, pricingInfo: matchingRegionalListing.pricingInfo, walletSpend: matchingRegionalListing.walletSpend };
        }
        // The dedicated per-place couple's-pick pricing query (useAccommodationPlacesPricing)
        // averages 2.7s and p95 7.6s — long enough that users see a price skeleton on the
        // featured "Couple's Pick" tile while regional tiles already show prices. When the
        // same hotel appears in the regional listings, its pricing has already loaded; surface
        // it immediately and let the per-place query overwrite when it lands.
        if (!accommodation.pricingInfo.values && matchingRegionalListing?.pricingInfo.values) {
          return { ...accommodation, ...updates, pricingInfo: matchingRegionalListing.pricingInfo, walletSpend: null };
        }
        return { ...accommodation, ...updates, walletSpend: null };
      }),
      ...regionalListings.filter(listing => !!listing.joyPlaceId && !couplePickJoyPlaceIds.has(listing.joyPlaceId)),
    ];
  }, [eventAccommodations, backgroundListings, cachedListings, skipCouplePicks, isInitialLoad, shouldUseBackgroundListings]);

  // Notify parent of success/error states
  // Using stable callbacks to prevent infinite loops when error persists
  useEffect(() => {
    if (regionalError) {
      onError?.(regionalError);
    } else if (areQueriesSettled && cachedListings.length > 0) {
      onSuccess?.();
    }
  }, [regionalError, areQueriesSettled, cachedListings.length, onError, onSuccess]);

  return {
    cachedListings,
    listings: consolidatedListings,
    hasAccommodationPlacesByCouple: eventAccommodations.length > 0,
    isFetchingListings: isFetchingListings || isFetchingEventAccommodations,
    isFetchingListingPrices: false,
    hasAnyError,
    hasApiError,
    isCombinedLoading: !areQueriesSettled,
    isUsingBackgroundListings: shouldUseBackgroundListings,
    pricingError: isViewingCouplesPicks ? errorFetchingAccommodationPrices : null,
  };
};
